<template>
  <div class="app-root">
    <!-- Boot loader: the #app placeholder in index.html only covers up to Vue
         root mount; once App mounts, <router-view/> is empty (white) while the
         auth restore + blocking router guard resolve the first navigation. This
         overlay bridges that window (root mount → first route resolved) so the
         user never sees a blank page. Hidden on the first router afterEach. -->
    <transition name="boot-fade">
      <div v-if="booting" class="boot-loading" role="status" aria-live="polite">
        <div class="boot-loading__spinner" aria-hidden="true"></div>
        <div class="boot-loading__text">กำลังโหลดข้อมูลระบบ กรุณารอสักครู่...</div>
      </div>
    </transition>
    <router-view/>
    <connection-banner />
    <dev-tools-panel />
    <transition name="network-popup">
      <div
        v-if="networkOfflineVisible"
        class="network-offline-popup"
        role="status"
        aria-live="polite"
      >
        <span class="mdi mdi-wifi-off network-offline-popup__icon" aria-hidden="true"></span>
        <span class="network-offline-popup__text">
          ไม่มีการเชื่อมต่ออินเทอร์เน็ต กำลังพยายามเชื่อมต่อใหม่
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import { mapState } from 'vuex'
import fc from '@/firebaseConfig'
import {
  startConnectionMonitor,
  stopConnectionMonitor,
} from '@/libs/firestoreHealth'
import ConnectionBanner from '@/components/admin/ConnectionBanner.vue'
import DevToolsPanel from '@/components/public/dev-tools-panel.vue'

export default {
  name: 'App',
  components: { ConnectionBanner, DevToolsPanel },
  data () {
    return { booting: true }
  },
  computed: {
    ...mapState({
      networkState: (state) => state.connectionHealth.network,
    }),
    networkOfflineVisible() {
      return this.networkState === 'offline'
    },
  },
  created () {
    this.authUnsub = fc.auth.onAuthStateChanged((user) => {
      if (user) {
        this.stampActive(user.uid)
        this.beginMonitoring()
      } else stopConnectionMonitor()
    })
    // Drop the boot loader once the first navigation resolves (guard + route
    // component mounted). Fallback timeout guards against a navigation that
    // never completes so the overlay can't get stuck.
    this.removeAfterEach = this.$router.afterEach(() => { this.booting = false })
    this.bootTimeout = setTimeout(() => { this.booting = false }, 20000)
  },
  beforeDestroy () {
    if (this.authUnsub) this.authUnsub()
    if (this.removeAfterEach) this.removeAfterEach()
    if (this.bootTimeout) clearTimeout(this.bootTimeout)
    stopConnectionMonitor()
  },
  methods: {
    // Stamp last-active for active/inactive tracking. Fires on every app load
    // (incl. restored sessions), throttled to once / 12h via localStorage so a
    // long-lived persistent session still counts as active. Best-effort.
    stampActive (uid) {
      try {
        const key = 'lastActive:' + uid
        const last = Number(localStorage.getItem(key) || 0)
        if (Date.now() - last < 12 * 60 * 60 * 1000) return
        localStorage.setItem(key, String(Date.now()))
        fc.users
          .doc(uid)
          .update({ lastActiveAt: firebase.firestore.FieldValue.serverTimestamp() })
          .catch(() => {})
      } catch (e) { /* best-effort: never block app start */ }
    },
    beginMonitoring () {
      // Passive observation: the network watcher only listens to browser
      // online/offline events and pipes them into Vuex. Listener errors
      // (the real signal for backend trouble) are reported by
      // `libs/firestoreListener.js` directly to the Vuex module.
      startConnectionMonitor({})
    },
  }
}
</script>

<style lang="scss">
html {
  scroll-behavior: smooth;
}

.app-root {
  height: 100%;
}

/* Boot spinner — mirrors the static spinner in public/index.html so the handoff
   from placeholder to this overlay at Vue mount is seamless (no white flash). */
.boot-loading {
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  font-family: "Noto Sans", "Roboto", sans-serif;
}

.boot-loading__spinner {
  width: 52px;
  height: 52px;
  border: 4px solid #f0e3e3;
  border-top-color: #c0392b;
  border-radius: 50%;
  animation: boot-spin 0.9s linear infinite;
}

.boot-loading__text {
  margin-top: 18px;
  font-size: 15px;
  color: #8a8a8a;
}

@keyframes boot-spin {
  to { transform: rotate(360deg); }
}

.boot-fade-leave-active {
  transition: opacity 200ms ease;
}

.boot-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .boot-loading__spinner { animation-duration: 1.8s; }
  .boot-fade-leave-active { transition: none; }
}

.network-offline-popup {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 48px);
  padding: 12px 20px;
  border-radius: 8px;
  background: #323232;
  color: #ffffff;
  font-family: "Noto Sans Thai", "Noto Sans", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.15),
    0 10px 15px -3px rgba(0, 0, 0, 0.12),
    0 20px 25px -5px rgba(0, 0, 0, 0.06);
  pointer-events: none;
}

.network-offline-popup__icon {
  color: #ffb74d;
  font-size: 20px;
  line-height: 1;
}

.network-offline-popup__text {
  white-space: nowrap;
}

.network-popup-enter-active,
.network-popup-leave-active {
  transition: opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.network-popup-enter,
.network-popup-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

@media (prefers-reduced-motion: reduce) {
  .network-popup-enter-active,
  .network-popup-leave-active {
    transition: none;
  }
}

@media (max-width: 480px) {
  .network-offline-popup {
    bottom: 16px;
    right: 16px;
    left: 16px;
    justify-content: center;
  }
  .network-offline-popup__text {
    white-space: normal;
  }
}
</style>
